import isNil from 'lodash/isNil'

const htmlFormsTagId = 'wf_html_forms'
const emails = ['email', 'mail']
const fullnames = ['fullname', 'full_name', 'full-name', 'name']
const firstnames = ['firstname', 'fname', 'first_name', 'first-name']
const lastnames = ['lastname', 'lname', 'last_name', 'last-name']
const phones = [
  'phone',
  'phonenumber',
  'phone_number',
  'phone-number',
  'mobilenumber',
  'mobile_number',
  'mobile-number',
  'mobilephone',
  'mobile_phone',
  'mobile-phone'
]

const findKey = (keys, field) =>
  keys.find(k => {
    const key = k.toLowerCase()
    return key.includes(field) || field.includes(key)
  })

const setFields = (formData, fields, value) => {
  const keys = Array.from(formData.keys())

  return fields.some(field => {
    const key = findKey(keys, field)

    if (key) {
      formData.set(key, value)
      return true
    }

    return false
  })
}

const generateFormData = (form, viewer) => {
  const formData = new FormData(form)
  const email = viewer.email || ''
  const first_name = viewer.first_name || ''
  const last_name = viewer.last_name || ''
  const phone = viewer.phone || ''
  const data = [
    { fields: emails, value: email },
    { fields: fullnames, value: `${first_name} ${last_name}`.trim() },
    { fields: firstnames, value: first_name },
    { fields: lastnames, value: last_name },
    { fields: phones, value: phone }
  ]
  data.forEach(({ fields, value }) => setFields(formData, fields, value))

  return formData
}

const createScriptTag = (url, html) => {
  const div = document.createElement('script')
  div.type = 'text/javascript'
  div.src = url
  div.innerHTML = html
  return div
}

const createElements = html => {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.children
}

class CustomHtmlForms {
  constructor() {
    this.forms = {}
    this.render = this.render.bind(this)
    this.track = this.track.bind(this)
    this.trackLeads = this.trackLeads.bind(this)
    this.trackRegistrants = this.trackRegistrants.bind(this)
    this.validateTriggerOptions = this.validateTriggerOptions.bind(this)
    this.validateVideo = this.validateVideo.bind(this)
    this.validateSource = this.validateSource.bind(this)
    this.sendTrackData = this.sendTrackData.bind(this)
    this.addForm = this.addForm.bind(this)
  }

  render(webinarCid, integrationsHtmlForms) {
    try {
      const id = `${htmlFormsTagId}_${webinarCid}`
      if (document.body.querySelector(`div#${id}`) || integrationsHtmlForms.length === 0) return

      const div = document.createElement('div')
      div.id = id
      div.style.display = 'none'
      document.body.append(div)

      integrationsHtmlForms.forEach(form => {
        const el = createElements(form.form)[0]
        const scripts = [...createElements(form.scripts)].map(s => createScriptTag(s.src, s.innerHTML))
        div.append(el)
        scripts.forEach(script => document.body.append(script))

        this.addForm(webinarCid, { triggers: form.triggers, form: el, scripts })
      })
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.log('Custom Html Forms Error: ', error)
    }
  }

  track(trigger, webinarCid, viewer) {
    try {
      const forms = (this.forms[webinarCid] || [])
        .filter(form => form.triggers.some(t => t.trigger === trigger && this.validateTriggerOptions(t, viewer)))
        .map(form => form.form)

      return Promise.all(this.sendTrackData(forms, viewer))
    } catch {
      return Promise.resolve()
    }
  }

  trackLeads(webinarCid, viewer) {
    return this.track('new_lead', webinarCid, viewer)
  }

  trackRegistrants(webinarCid, viewer) {
    return this.track('registered_to_webinar', webinarCid, viewer)
  }

  validateTriggerOptions(trigger, viewer) {
    return this.validateVideo(viewer.video_id, trigger.video_id) && this.validateSource(viewer.source, trigger.sources)
  }

  validateVideo(viewerVideoId, videoId) {
    return isNil(videoId) || videoId === 'all' || parseInt(videoId) === parseInt(viewerVideoId)
  }

  validateSource(viewerSource, sources) {
    return Array.isArray(sources) && sources.length > 0 ? sources.includes(viewerSource) : true
  }

  sendTrackData(forms, viewer) {
    return forms.map(form => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout
      const formData = generateFormData(form, viewer)

      return fetch(form.action, {
        method: form.method,
        body: new URLSearchParams(Object.fromEntries(formData.entries())).toString(),
        mode: 'no-cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        signal: controller.signal
      })
        .then(() => {
          clearTimeout(timeoutId)
        })
        .catch(error => {
          // oxlint-disable-next-line no-console
          console.log('Custom Html Forms Request Error: ', error)
        })
    })
  }

  addForm(webinarCid, data) {
    if (!this.forms[webinarCid]) this.forms[webinarCid] = []
    this.forms[webinarCid].push(data)
  }
}

export default new CustomHtmlForms()
